import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Badge,
  type BadgeTone,
  Banner,
  Button,
  Card,
  CardBody,
  EmptyState,
  Field,
  Input,
  Select,
} from "@jotty/ui";

import { ApiError, meetings, type Meeting, type MeetingStatus } from "~/lib/api";
import { useAuthStore } from "~/lib/auth-store";

export const Route = createFileRoute("/_authed/")({
  component: HomePage,
});

const STATUSES: ReadonlyArray<MeetingStatus | "all"> = [
  "all",
  "scheduled",
  "in_progress",
  "completed",
  "failed",
  "archived",
];

const statusTone: Record<MeetingStatus, BadgeTone> = {
  scheduled: "neutral",
  in_progress: "warning",
  completed: "success",
  failed: "danger",
  archived: "neutral",
  no_transcript: "neutral",
  too_short: "neutral",
  summary_failed: "neutral",
  summarized: "success",
  recording: "warning",
};

function HomePage() {
  const tenants = useAuthStore((s) => s.tenants);
  const activeTenant = tenants[0]; // _authed already redirects to /onboarding when this is undefined.

  const [items, setItems] = useState<Meeting[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<MeetingStatus | "all">("all");
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  // Refetch on tenant change or filter change.
  useEffect(() => {
    if (!activeTenant) return;
    let cancelled = false;
    setError(null);
    setItems(null);
    (async () => {
      try {
        const opts = statusFilter === "all" ? undefined : { status: statusFilter };
        const res = await meetings.list(activeTenant.id, opts);
        if (!cancelled) setItems(res.meetings ?? []);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load meetings");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTenant?.id, statusFilter]);

  // Title-based client-side filter on top of server-side status filter — keeps
  // the UI responsive without a per-keystroke API call.
  const filtered = useMemo(() => {
    if (!items) return null;
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((m) => m.title.toLowerCase().includes(q));
  }, [items, query]);

  if (!activeTenant) return null;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-[color:var(--color-text)]">
            Meetings
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-text-muted)]">
            Every meeting in <span className="font-medium">{activeTenant.name}</span>.
          </p>
        </div>
        <Button variant="primary" onClick={() => setShowCreate((s) => !s)}>
          {showCreate ? "Cancel" : "New meeting"}
        </Button>
      </header>

      {showCreate ? (
        <CreateMeetingForm
          tenantId={activeTenant.id}
          onCreated={(m) => {
            setItems((prev) => (prev ? [m, ...prev] : [m]));
            setShowCreate(false);
          }}
        />
      ) : null}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Search" className="flex-1 min-w-[200px]">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter by title" />
        </Field>
        <Field label="Status">
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s === "all" ? "All statuses" : prettyStatus(s)}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {filtered === null && !error ? (
        <p className="text-sm text-[color:var(--color-text-muted)]">Loading…</p>
      ) : filtered && filtered.length === 0 ? (
        <EmptyState
          title={statusFilter === "all" ? "No meetings yet" : `No ${prettyStatus(statusFilter as MeetingStatus)} meetings`}
          description={statusFilter === "all" ? "Schedule a meeting or connect your calendar." : undefined}
          action={statusFilter === "all" ? <Button onClick={() => setShowCreate(true)}>New meeting</Button> : undefined}
        />
      ) : (
        <ul className="grid gap-3">
          {filtered!.map((m) => (
            <li key={m.id}>
              <Link to="/meetings/$id" params={{ id: m.id }} className="block">
                <Card className="transition-colors hover:border-[color:var(--color-border-strong)]">
                  <CardBody className="flex items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="truncate font-medium text-[color:var(--color-text)]">{m.title}</h3>
                        <Badge tone={statusTone[m.status]}>{prettyStatus(m.status)}</Badge>
                      </div>
                      <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
                        {m.attendees.length === 0
                          ? "No attendees"
                          : `${m.attendees.length} attendee${m.attendees.length === 1 ? "" : "s"}`}
                        {" · "}
                        {m.started_at ? new Date(m.started_at).toLocaleString() : `created ${new Date(m.created_at).toLocaleDateString()}`}
                      </p>
                    </div>
                  </CardBody>
                </Card>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CreateMeetingForm({
  tenantId,
  onCreated,
}: {
  tenantId: string;
  onCreated: (m: Meeting) => void;
}) {
  const [title, setTitle] = useState("");
  const [attendees, setAttendees] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const m = await meetings.create(tenantId, {
        title,
        attendees: attendees
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      });
      onCreated(m);
      setTitle("");
      setAttendees("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create meeting");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="mb-4">
      <form onSubmit={handleSubmit} className="space-y-4 p-6">
        <Field label="Title" required>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="Q4 roadmap review" />
        </Field>
        <Field label="Attendees" helper="Comma-separated emails. Used as recipients for summary email delivery.">
          <Input
            value={attendees}
            onChange={(e) => setAttendees(e.target.value)}
            placeholder="alice@example.com, bob@example.com"
          />
        </Field>
        {error ? <p className="text-sm text-[color:var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Creating…" : "Create meeting"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function prettyStatus(s: MeetingStatus): string {
  return s.replace("_", " ");
}
